import { Link, useLocation, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiArrowLeft } from '@mdi/js'
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
} from '../ui/sidebar'
import { SECTION_ICONS } from '../../lib/section-icons'
import { useGroupSummary, type Level, type Period } from '../../api/groupSteering'
import { useLocale } from '../../contexts/LocaleContext'
import { useFormatters } from '../../lib/format'
import { langOf, text } from './format'

/** Whether a pathname is inside the group space of the organization. */
export function isGroupSpacePath(pathname: string, org: string): boolean {
    const prefix = `/${org}/group`
    return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

/**
 * The sidebar of the group space (spec 2026-09-11-01, "un espace, pas une
 * page"): three groups — the three readings of one snapshot, the families
 * with their exception counts, the resources that make the numbers
 * contestable — and a footer with the freshness and the access profile.
 * The profile is read from the API, never chosen in the interface.
 */
export function GroupSidebar({ org }: { org: string }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const { formatDate } = useFormatters()
    const lang = langOf(locale)
    const location = useLocation()
    const search = useSearch({ strict: false }) as { period?: Period; level?: Level; family?: string }
    const period: Period = search.period ?? '90d'
    const level: Level = search.level ?? 'agency'
    const summary = useGroupSummary(org, period, level)
    const families = summary.data?.families ?? []
    const profile = summary.data?.profile
    const isActive = (path: string) => location.pathname === `/${org}${path}`
    const baseSearch = { period, level }

    return (
        <Sidebar data-testid="group-sidebar">
            <SidebarHeader>
                <div className="px-2 py-2">
                    <Link
                        to="/$org"
                        params={{ org }}
                        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                        data-testid="group-back-to-workspace"
                    >
                        <AppIcon path={mdiArrowLeft} size="xs" />
                        {t('space.back_to_workspace')}
                    </Link>
                    <div className="mt-1 flex items-center gap-2">
                        <AppIcon path={SECTION_ICONS.group_steering} size="md" />
                        <span className="text-base font-semibold text-foreground tracking-tight">{t('space.title')}</span>
                    </div>
                    {summary.data?.scope?.name && (
                        <div className="text-xs text-muted-foreground truncate" data-testid="group-scope-name">
                            {summary.data.scope.name}
                        </div>
                    )}
                </div>
            </SidebarHeader>
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel>{t('nav.steering')}</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild isActive={isActive('/group')}>
                                    <Link to="/$org/group" params={{ org }} search={{ ...baseSearch, family: undefined, kpi: undefined }} data-testid="group-nav-consolidated">
                                        {t('nav.consolidated')}
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild isActive={isActive('/group/perimeters')}>
                                    <Link to="/$org/group/perimeters" params={{ org }} search={{ ...baseSearch, family: undefined, kpi: undefined, selected: undefined, q: undefined }} data-testid="group-nav-perimeters">
                                        {t('nav.perimeters')}
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild isActive={isActive('/group/sites')}>
                                    <Link to="/$org/group/sites" params={{ org }} search={{ period }} data-testid="group-nav-sites">
                                        {t('nav.sites')}
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            {profile === 'group' && (
                                <SidebarMenuItem>
                                    <SidebarMenuButton asChild isActive={isActive('/group/governance')}>
                                        <Link to="/$org/group/governance" params={{ org }} search={{ period }} data-testid="group-nav-governance">
                                            {t('nav.governance')}
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            )}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
                <SidebarGroup>
                    <SidebarGroupLabel>{t('nav.families')}</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {families
                                .filter((f) => f.tiles.length > 0)
                                .map((f) => (
                                    <SidebarMenuItem key={f.code}>
                                        <SidebarMenuButton asChild isActive={isActive('/group') && search.family === f.code}>
                                            <Link to="/$org/group" params={{ org }} search={{ ...baseSearch, family: f.code, kpi: undefined }} data-testid={`group-nav-family-${f.code}`}>
                                                {text(f.label, lang)}
                                            </Link>
                                        </SidebarMenuButton>
                                        <SidebarMenuBadge
                                            className={f.exceptions_count > 0 ? 'text-destructive' : 'text-muted-foreground'}
                                            title={f.exceptions_count > 0 ? t('nav.exceptions', { count: f.exceptions_count }) : t('nav.no_exception')}
                                            data-testid={`group-nav-family-${f.code}-badge`}
                                        >
                                            {f.exceptions_count > 0 ? f.exceptions_count : '·'}
                                        </SidebarMenuBadge>
                                    </SidebarMenuItem>
                                ))}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
                <SidebarGroup>
                    <SidebarGroupLabel>{t('nav.resources')}</SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild isActive={isActive('/group/definitions')}>
                                    <Link to="/$org/group/definitions" params={{ org }} data-testid="group-nav-definitions">
                                        {t('nav.definitions')}
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild isActive={isActive('/group/criteria')}>
                                    <Link to="/$org/group/criteria" params={{ org }} data-testid="group-nav-criteria">
                                        {t('nav.criteria')}
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <SidebarMenuItem>
                                <SidebarMenuButton asChild isActive={isActive('/group/access')}>
                                    <Link to="/$org/group/access" params={{ org }} search={{ period, level }} data-testid="group-nav-access">
                                        {t('nav.access')}
                                    </Link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>
            <SidebarFooter>
                <div className="px-2 py-2 text-[11px] text-muted-foreground space-y-0.5" data-testid="group-freshness">
                    {summary.data?.freshness?.computed_at && (
                        <div>{t('space.freshness', { date: formatDate(summary.data.freshness.computed_at, { dateStyle: 'short', timeStyle: 'short' }) })}</div>
                    )}
                    {summary.data?.freshness?.history_since && (
                        <div>{t('space.history_since', { date: formatDate(summary.data.freshness.history_since, { dateStyle: 'short' }) })}</div>
                    )}
                    {profile && (
                        <div className="text-foreground" data-testid="group-profile">
                            {profile === 'group' ? t('space.profile_group') : t('space.profile_entity')}
                        </div>
                    )}
                </div>
            </SidebarFooter>
        </Sidebar>
    )
}
